import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Avatar,
  Box,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import { roundToOneDecimal } from '../components/room/utils';
import { fetchGet } from '../api/common';
import { useNavigate } from 'react-router-dom';
import { StyledPaperItem } from '../common/mui-components';
import { resizeImage } from '../api/cloudinary';

type HandicapRecord = {
  profileId: string;
  opponentProfileId: string;
  opponentAvatarPublicId: string;
  opponentUsername: string;
  opponentGhostChallengeStatus: string;
  wins: number;
  losses: number;
  ties: number;
};

type PropTypes = {
  close: () => void;
  open: boolean;
  profileId: string;
};

export const HandicapWinratesDialog = ({
  close,
  open,
  profileId,
}: PropTypes) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [overallWinrate, setOverallWinrate] = useState<number>();
  const [handicapRecords, setHandicapRecords] = useState<HandicapRecord[]>();

  useEffect(() => {
    if (!open) return;
    fetchHandicapRecords();
  }, [open]);

  const fetchHandicapRecords = async () => {
    const res: HandicapRecord[] = await fetchGet(
      `/games/handicap/records/${profileId}`
    );
    const sorted = res.sort(
      (a, b) => b.wins + b.losses + b.ties - (a.wins + a.losses + a.ties)
    );
    calculateOverallWinrate(res);
    setHandicapRecords((prev) => sorted);
  };

  const calculateOverallWinrate = (records: HandicapRecord[]) => {
    let wins = 0;
    let losses = 0;
    records.forEach((r) => {
      wins = wins + r.wins;
      losses = losses + r.losses;
    });
    const winrate = wins / (wins + losses);
    setOverallWinrate(Math.round(winrate * 100));
  };

  const handleUsernameClick = async (e: any, profileId: string) => {
    e.stopPropagation();
    close();
    navigate(`/profile/overview/${profileId}`);
  };

  const getProgressValue = (record: HandicapRecord) => {
    if (!handicapRecords) return 0;
    const totalMatches = record.wins + record.losses + record.ties;
    const highestMatchTotal =
      handicapRecords[0].wins +
      handicapRecords[0].losses +
      handicapRecords[0].ties;
    return (totalMatches / highestMatchTotal) * 100;
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack spacing={2}>
          <Typography variant="h5" textAlign={'center'} letterSpacing={5}>
            HANDICAPPED GAMES WIN RATES
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ width: '100%', height: '100%' }}>
        <Grid item xs={12} sm={12} className="WIN-RATES">
          <Stack spacing={2} mb={4}>
            <Typography variant="h6" textAlign={'center'}>
              Your overall winrate is:
            </Typography>
            <Typography variant="h5" textAlign={'center'} fontWeight={600}>
              {overallWinrate}%
            </Typography>
          </Stack>
          <StyledPaperItem style={{ height: '100%' }}>
            <Stack direction="row">
              <TableContainer component={Paper}>
                <Table aria-label="simple table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Player</TableCell>
                      <TableCell>Matches</TableCell>
                      <TableCell>Win Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {handicapRecords
                      ?.slice(0, Math.ceil(handicapRecords.length / 2))
                      .map((record) => (
                        <TableRow hover key={record.opponentProfileId}>
                          <TableCell>
                            <Stack direction={'row'} spacing={2}>
                              <Avatar
                                src={resizeImage(
                                  record.opponentAvatarPublicId,
                                  50,
                                  50
                                )}
                                variant="rounded"
                                sx={{
                                  margin: '2px',
                                  height: 25,
                                  width: 25,
                                }}
                              />
                              <Link
                                component="button"
                                variant="body2"
                                onClick={(e) =>
                                  handleUsernameClick(
                                    e,
                                    record.opponentProfileId
                                  )
                                }
                                textAlign={'left'}
                              >
                                <Typography
                                  sx={{
                                    lineHeight: '25px',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    width: '130px',
                                  }}
                                >
                                  {' '}
                                  {record.opponentUsername}
                                </Typography>
                              </Link>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {record.wins + record.losses + record.ties}
                            <LinearProgress
                              variant="determinate"
                              value={getProgressValue(record)}
                            />
                          </TableCell>
                          <TableCell>
                            {roundToOneDecimal(
                              (record.wins /
                                (record.wins + record.losses + record.ties)) *
                                100
                            )}
                            %
                            <LinearProgress
                              variant="determinate"
                              value={roundToOneDecimal(
                                (record.wins /
                                  (record.wins + record.losses + record.ties)) *
                                  100
                              )}
                              color={'success'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider orientation="vertical" flexItem />
              <TableContainer component={Paper}>
                <Table aria-label="simple table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Player</TableCell>
                      <TableCell>Matches</TableCell>
                      <TableCell>Win Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {handicapRecords
                      ?.slice(
                        Math.ceil(handicapRecords.length / 2),
                        handicapRecords.length
                      )
                      .map((record) => (
                        <TableRow hover key={record.opponentProfileId}>
                          <TableCell>
                            <Stack direction={'row'} spacing={2}>
                              <Avatar
                                src={resizeImage(
                                  record.opponentAvatarPublicId,
                                  50,
                                  50
                                )}
                                variant="rounded"
                                sx={{
                                  margin: '2px',
                                  height: 25,
                                  width: 25,
                                }}
                              />
                              <Link
                                component="button"
                                variant="body2"
                                onClick={(e) =>
                                  handleUsernameClick(
                                    e,
                                    record.opponentProfileId
                                  )
                                }
                                textAlign={'left'}
                              >
                                <Typography
                                  sx={{
                                    lineHeight: '25px',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    width: '130px',
                                  }}
                                >
                                  {' '}
                                  {record.opponentUsername}
                                </Typography>
                              </Link>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {record.wins + record.losses + record.ties}
                            <LinearProgress
                              variant="determinate"
                              value={getProgressValue(record)}
                            />
                          </TableCell>
                          <TableCell>
                            {roundToOneDecimal(
                              (record.wins /
                                (record.wins + record.losses + record.ties)) *
                                100
                            )}
                            %
                            <LinearProgress
                              variant="determinate"
                              value={roundToOneDecimal(
                                (record.wins /
                                  (record.wins + record.losses + record.ties)) *
                                  100
                              )}
                              color={'success'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </StyledPaperItem>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="text" size="large" onClick={close} fullWidth>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};
